import { MachineLibraries } from './machine-libraries.tsx';
import { MachineStorage } from './machine-storage.tsx';
import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode, type RefObject } from 'react';

import { HEARTBEAT_STALE_MS, runnerLiveness, stageTimeoutMs } from '@nightshift/ui-liveness';
import type {
  MachineAttemptView,
  MachineCapacityPathView,
  MachineCapacityPolicyView,
  MachineCapacityView,
  MachineHardwareView,
  MachineList,
  MachineListRow,
  MachineWindowStats,
  MintedMachineResponse,
  UiProject,
} from '@nightshift/ui-types';

import { Empty, Kicker, Spinner, StateChip } from '../components/primitives.tsx';
import { api, type SessionUser } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import { absoluteTime, compactDuration, plural, relativeShort, tokens, untilShort } from '../lib/format.ts';
import { spendFigure } from '../lib/ops.ts';
import { canWriteSettings } from '../lib/privilege.ts';
import { useSettingsAutosave } from '../lib/settings-autosave.tsx';
import { ProjectAccessPicker } from '../components/project-access-picker.tsx';
import './machines-settings.css';

/** Matches MAX_DESIRED_WORKERS on the server and the runner's own cap. */
const MAX_WORKERS = 16;
const MAX_SIDECARS = 32;

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function downloadText(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

type LampTone = 'ok' | 'attention' | 'quiet';

function lampFor(row: MachineListRow, now: number): { tone: LampTone; label: string } {
  const liveness = runnerLiveness(
    { status: row.health, lastHeartbeatAt: row.lastSeen },
    now,
    HEARTBEAT_STALE_MS,
  );
  if (row.health === 'unhealthy') return { tone: 'attention', label: 'unhealthy' };
  if (row.health === 'online' && !liveness.live) return { tone: 'attention', label: 'stale' };
  // Paused reads as a deliberate state, not a fault: the lamp stays quiet and the word says
  // why the machine has nothing running.
  if (row.paused) return { tone: 'quiet', label: liveness.live ? 'paused' : `paused · ${row.health}` };
  // Held by its capacity floors: alive, but offered nothing until a path clears its floor.
  if (row.capacityHeldAt !== null) return { tone: 'attention', label: 'held by floor' };
  if (liveness.live) return { tone: 'ok', label: 'live' };
  return { tone: 'quiet', label: row.health };
}

function lampColour(tone: LampTone): string {
  return tone === 'ok' ? 'var(--good)' : tone === 'attention' ? 'var(--warn)' : 'var(--text-4)';
}

function ticketHref(key: string): string {
  const params = new URLSearchParams(window.location.search);
  params.set('ticket', key);
  return `${window.location.pathname}?${params.toString()}`;
}

function meanDuration(stats: MachineWindowStats): string {
  if (stats.meanDurationMs == null) return '—';
  return compactDuration(stats.meanDurationMs);
}

function lastWork(stats: MachineWindowStats, now: number): { value: string; note: string | undefined } {
  if (stats.lastFinishedAt == null) return { value: '—', note: undefined };
  const age = `${relativeShort(stats.lastFinishedAt, now)} ago`;
  return {
    value: stats.lastTicketKey ?? age,
    note: stats.lastTicketKey ? age : undefined,
  };
}

/* ── plan capacity ───────────────────────────────────────────────────────────────────── */

const PROVIDER_NAMES: Record<MachineCapacityView['provider'], string> = { claude: 'Claude', codex: 'Codex' };

type Tone = 'good' | 'warn' | 'danger' | 'quiet';

/** The words a window is shown under. The provider's key stays in the title for the curious. */
function windowLabel(window: MachineCapacityView['windows'][number]): string {
  if (window.kind === 'model' && window.model) return `${window.model} · week`;
  if (window.kind === 'session' && window.model) return `${window.model} · 5h`;
  switch (window.name) {
    case 'five_hour':
    case 'session':
      return 'Session · 5h';
    case 'seven_day':
    case 'weekly':
      return 'Week';
    case 'extra_usage':
    case 'credits':
      return 'Extra usage';
    default:
      return window.name.replace(/_/g, ' ');
  }
}

/** Colour by what is left: green, amber at 30 or under, red at 10 or under. */
function remainingTone(remaining: number | null): Tone {
  if (remaining === null) return 'quiet';
  if (remaining <= 10) return 'danger';
  if (remaining <= 30) return 'warn';
  return 'good';
}

/** What routing sees as of `now`: a window whose reset has passed counts as full. */
function remainingNow(window: MachineCapacityView['windows'][number], now: number): number | null {
  if (window.resetsAt) {
    const at = Date.parse(window.resetsAt);
    if (Number.isFinite(at) && at <= now) return 100;
  }
  return window.remainingPercent;
}

/** Mirrors `countsTowardPlan` in core: credits and other-surface windows are shown, not counted. */
function countsTowardPlan(window: MachineCapacityView['windows'][number]): boolean {
  return window.kind === 'session' || window.kind === 'daily' || window.kind === 'weekly' || window.kind === 'monthly' || window.kind === 'model';
}

/** Mirrors `isPlanWideWindow` in core: a window measuring the plan, not one model's share. */
function isPlanWide(window: MachineCapacityView['windows'][number]): boolean {
  return countsTowardPlan(window) && window.model === null;
}

/**
 * The headline figure: the tightest *plan-wide* window.
 *
 * A per-model window never sets it. `Fable · week 1%` means Fable is spent for the week, and
 * writing `1% left` beside `Claude Max 20x` because of it says the account is finished when
 * its session has 87% and its week 29%. The per-model rows below carry their own figure and
 * their own `closed` chip, which is where that story belongs.
 */
function lowestRemaining(entry: MachineCapacityView, now: number): number | null {
  let lowest: number | null = null;
  for (const window of entry.windows) {
    if (!isPlanWide(window)) continue;
    const remaining = remainingNow(window, now);
    if (remaining === null) continue;
    if (lowest === null || remaining < lowest) lowest = remaining;
  }
  return lowest;
}

/** True when the figures count for routing: fresh, and the provider actually answered. */
function usable(entry: MachineCapacityView): boolean {
  return entry.fresh && (entry.status === 'available' || entry.status === 'exhausted');
}

function statusWords(entry: MachineCapacityView): string {
  switch (entry.status) {
    case 'available':
      return entry.fresh ? 'available' : 'stale';
    case 'throttled':
      return 'rate limited';
    case 'exhausted':
      return 'exhausted';
    case 'unavailable':
      if (entry.error === 'no-credentials') return 'not signed in';
      if (entry.error === 'credentials-expired') return 'login expired';
      if (entry.error === 'api-key-login') return 'API key, no plan';
      if (entry.error?.startsWith('keychain')) return 'Keychain not readable';
      if (entry.error === 'proxy-disabled') return 'disabled in proxy';
      if (entry.error === 'no-proxy-accounts') return 'no proxy accounts';
      if (entry.error === 'proxy-auth-dir-unreadable') return 'proxy not readable';
      return 'unavailable';
    case 'error':
      return `no answer (${entry.error ?? 'error'})`;
  }
}

function headlineTone(entry: MachineCapacityView, lowest: number | null): Tone {
  if (!usable(entry)) return 'quiet';
  if (entry.status === 'exhausted') return 'danger';
  return remainingTone(lowest);
}

/** The key a window's floor is stored under for one account; mirrors `floorKey` in core. */
function floorKeyOf(entry: Pick<MachineCapacityView, 'provider' | 'account'>, window: Pick<MachineCapacityView['windows'][number], 'name'>): string {
  return `${providerFloorKeyOf(entry, window)}@${entry.account}`;
}

/** The provider-wide key: every account without a key of its own falls back to it. */
function providerFloorKeyOf(entry: Pick<MachineCapacityView, 'provider'>, window: Pick<MachineCapacityView['windows'][number], 'name'>): string {
  return `${entry.provider}:${window.name}`;
}

/**
 * True when this model is not offered on the machine: the orchestrator lists it closed (under
 * its floor, or spent), which is the same judgement routing made. Mirrors `closedModels` in
 * core; the labels match because both sides use the provider's own words for the model.
 */
function windowModelClosed(path: MachineCapacityPathView | undefined, window: MachineCapacityView['windows'][number]): boolean {
  if (!path || !path.open || window.model === null) return false;
  const model = window.model.toLowerCase();
  return path.closedModels.some((closed) => closed.toLowerCase() === model);
}

interface FloorNotchProps {
  /** The words for the slider: `Session · 5h floor`, `Fable · week floor`. */
  label: string;
  /** 0..100, or null for off. */
  value: number | null;
  editable: boolean;
  machine: string;
  onCommit: (next: number | null) => void;
}

const FLOOR_STEP = 5;

/**
 * The floor, drawn on the bar it judges: a hairline through the track with a handle above it.
 *
 * Drag the handle to move the floor; the value is committed on release so a drag is one
 * request. Arrow keys move it by a step when focused, Home parks it at "off", and a double
 * click switches it off. The handle sits in the space above the bar, which is what gives a
 * 6px track something big enough to grab. Every bar has a floor of its own: a plan-wide
 * window's floor closes the provider on this machine, a model window's floor closes that
 * model alone.
 */
function FloorNotch({ label, value, editable, machine, onCommit, track }: FloorNotchProps & { track: RefObject<HTMLElement | null> }) {
  const [draft, setDraft] = useState<number | null>(value);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!dragging) setDraft(value);
  }, [value, dragging]);
  const percentAt = (clientX: number): number | null => {
    const rect = track.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return draft;
    const raw = ((clientX - rect.left) / rect.width) * 100;
    const stepped = Math.round(Math.max(0, Math.min(100, raw)) / FLOOR_STEP) * FLOOR_STEP;
    return stepped === 0 ? null : stepped;
  };
  const commit = (next: number | null): void => {
    setDragging(false);
    setDraft(next);
    if (next !== value) onCommit(next);
  };
  const onPointerDown = (event: PointerEvent<HTMLSpanElement>): void => {
    if (!editable) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    setDraft(percentAt(event.clientX));
  };
  const onPointerMove = (event: PointerEvent<HTMLSpanElement>): void => {
    if (!dragging) return;
    setDraft(percentAt(event.clientX));
  };
  const onPointerUp = (event: PointerEvent<HTMLSpanElement>): void => {
    if (!dragging) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    commit(percentAt(event.clientX));
  };
  const onKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
    if (!editable) return;
    const current = draft ?? 0;
    let next: number | null | undefined;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = Math.min(100, current + FLOOR_STEP);
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = Math.max(0, current - FLOOR_STEP);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = 100;
    if (next === undefined) return;
    event.preventDefault();
    commit(next === 0 ? null : next);
  };
  const shown = draft ?? 0;
  return (
    <span
      className="floor-notch"
      data-off={draft === null ? 'true' : undefined}
      data-dragging={dragging ? 'true' : undefined}
      data-editable={editable ? 'true' : undefined}
      style={{ left: `${shown}%` }}
    >
      <span className="floor-notch-tick">{draft === null ? 'off' : `${draft}%`}</span>
      <span
        className="floor-notch-handle"
        role="slider"
        tabIndex={editable ? 0 : -1}
        aria-label={`${label} on ${machine}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={shown}
        aria-valuetext={draft === null ? 'off' : `${draft} percent`}
        aria-disabled={!editable}
        title={editable ? `${label}: drag, arrow keys, double-click for off` : label}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => setDragging(false)}
        onKeyDown={onKeyDown}
        onDoubleClick={() => editable && commit(null)}
      />
      {dragging ? (
        <span className="floor-notch-bubble">
          {label} <b>{draft === null ? 'off' : `${draft}%`}</b>
        </span>
      ) : null}
    </span>
  );
}

/** One bar with the floor notch of its class, when a floor judges it. */
function MeterTrack({ remaining, tone, notch }: { remaining: number; tone: Tone; notch?: FloorNotchProps }) {
  const track = useRef<HTMLSpanElement | null>(null);
  return (
    <span className="capacity-meter-track" data-notched={notch ? 'true' : undefined}>
      <span className="bar-track capacity-meter-bar" ref={track} aria-hidden>
        <span className="bar-fill" data-tone={tone} style={{ '--bar-fill': Math.max(0, Math.min(1, remaining / 100)) } as CSSProperties} />
      </span>
      {notch ? <FloorNotch {...notch} track={track} /> : null}
    </span>
  );
}

type CapacityWindowView = MachineCapacityView['windows'][number];

/** The floor that judges one window on this machine: the account's own key, else the provider-wide one. Null for off. */
function floorOf(policy: MachineCapacityPolicyView, entry: MachineCapacityView, window: CapacityWindowView): number | null {
  const own = policy.floors[floorKeyOf(entry, window)];
  const value = own !== undefined ? own : policy.floors[providerFloorKeyOf(entry, window)];
  return value !== undefined && value > 0 ? value : null;
}

/**
 * A plan's size in base allowances, from the multiplier its label carries: `Max 20x` is 20,
 * `Pro 1x` is 1, `Prolite 5x` is 5. Null for a label without one (`Max`, `Team`) or no plan.
 */
function planSize(entry: MachineCapacityView): number | null {
  const match = /(\d+)x$/.exec(entry.planLabel ?? '');
  return match ? Number(match[1]) : null;
}

/** The plan's name without its multiplier; the table prints the size on its own. */
function planName(entry: MachineCapacityView): string {
  return entry.planLabel ? entry.planLabel.replace(/\s*\d+x$/, '') : 'plan unknown';
}

/** A short tag that tells two accounts on the same plan apart. The account is already a one-way digest. */
function accountTag(entry: MachineCapacityView): string {
  return `#${entry.account.slice(0, 4)}`;
}

/**
 * What an account has left as of `now`, as routing judges it: its tightest plan-wide window,
 * and nothing while the provider says the plan is exhausted and no window has reset since.
 * Mirrors `judgeCapacityReport` in core.
 */
function accountRemaining(entry: MachineCapacityView, now: number): number | null {
  const lowest = lowestRemaining(entry, now);
  if (entry.status !== 'exhausted') return lowest;
  const reset = entry.windows.some((window) => isPlanWide(window) && window.resetsAt !== null && Date.parse(window.resetsAt) <= now);
  return reset && lowest !== null && lowest > 0 ? lowest : 0;
}

/** True when a counted account has a plan-wide window under this machine's floor for it. */
function underFloor(policy: MachineCapacityPolicyView, entry: MachineCapacityView, now: number): boolean {
  if (!usable(entry)) return false;
  return entry.windows.some((window) => {
    if (!isPlanWide(window)) return false;
    const floor = floorOf(policy, entry, window);
    const remaining = remainingNow(window, now);
    return floor !== null && remaining !== null && remaining < floor;
  });
}

/**
 * The floor drawn on an account's segment of the summary bar. The segment's fill is the
 * tightest window, so the tick is that window's floor, unless another window is under its own
 * floor: then that floor, which always sits right of the fill and reads as "under".
 */
function segmentFloor(policy: MachineCapacityPolicyView, entry: MachineCapacityView, now: number): number | null {
  let under: { deficit: number; floor: number } | null = null;
  let tightest: { remaining: number; floor: number | null } | null = null;
  for (const window of entry.windows) {
    if (!isPlanWide(window)) continue;
    const remaining = remainingNow(window, now);
    if (remaining === null) continue;
    const floor = floorOf(policy, entry, window);
    if (floor !== null && remaining < floor && (under === null || floor - remaining > under.deficit)) under = { deficit: floor - remaining, floor };
    if (tightest === null || remaining < tightest.remaining) tightest = { remaining, floor };
  }
  return under?.floor ?? tightest?.floor ?? null;
}

/** Why an account's figures are not counted, in the fewest words. */
function uncountedWords(entry: MachineCapacityView, now: number): string {
  if (entry.status === 'available' || entry.status === 'exhausted') return `figures ${relativeShort(entry.collectedAt, now)} old`;
  return statusWords(entry);
}

const CAPACITY_PROVIDERS = ['claude', 'codex'] as const;
type CapacityProvider = (typeof CAPACITY_PROVIDERS)[number];

interface CapacityIssue {
  tone: 'danger' | 'quiet';
  text: string;
}

/** One provider on one machine, as the fleet table's row shows it. */
interface ProviderSummary {
  entries: MachineCapacityView[];
  /** What is left across the counted accounts, each weighted by its plan size, in percent; null when none counts. */
  figure: number | null;
  /** The same in base allowances: 1 is a whole 1x plan. Null when none counts. */
  left: number | null;
  /** Plan sizes in base allowances, every account's and the counted ones'. Null when no account names its size. */
  size: { total: number; counted: number } | null;
  /** Bar length in base allowances. An account of unknown size counts as one. */
  weight: number;
  issues: CapacityIssue[];
}

/** Base allowances to one decimal: `24.7x`. */
function allowances(value: number): string {
  return `${(Math.round(value * 10) / 10).toFixed(1)}x`;
}

/**
 * Summarise one provider's accounts on a machine.
 *
 * The figure is what is left in base allowances: a 20x account half used beside two unused 5x
 * accounts is 20x left of 30x, 67% of it. Only accounts whose figures count (`usable`) enter it;
 * a rate-limited or out-of-date account is drawn with its last figures and named in the note,
 * never folded into the number. This is deliberately not routing's number, which ranks a
 * machine on its best usable account (`combineCapacityVerdicts`); it answers "how much is
 * left here", and the floors answer "may work run here".
 */
function summariseProvider(row: MachineListRow, provider: CapacityProvider, now: number): ProviderSummary {
  const entries = row.capacity.filter((entry) => entry.provider === provider);
  const counted = entries.filter(usable);
  // Weighted throughout by plan size, an account of unknown size as one, so the allowances
  // left, the counted plans and the bar's length are on the same scale.
  let sum = 0;
  let countedWeight = 0;
  let sized = false;
  for (const entry of entries) {
    const size = planSize(entry);
    if (size !== null) sized = true;
    if (!usable(entry)) continue;
    const remaining = accountRemaining(entry, now);
    if (remaining === null) continue;
    sum += (size ?? 1) * remaining;
    countedWeight += size ?? 1;
  }
  const weight = entries.reduce((total, entry) => total + (planSize(entry) ?? 1), 0);

  const issues: CapacityIssue[] = [];
  const many = entries.length > 1;
  const path = row.capacityPaths.find((candidate) => candidate.harness === provider);
  if (path && !path.open && counted.length > 0) issues.push({ tone: 'danger', text: 'closed by floor' });
  else {
    const under = counted.filter((entry) => underFloor(row.capacityPolicy, entry, now)).length;
    if (under > 0) issues.push({ tone: 'danger', text: many ? `${under} under floor` : 'under floor' });
  }
  for (const model of path?.open ? path.closedModels : []) issues.push({ tone: 'danger', text: `${model} closed` });
  const spent = counted.filter((entry) => accountRemaining(entry, now) === 0);
  if (spent.length > 0) {
    const reset = spent.length === 1 ? nextReset(spent[0]!, now) : null;
    issues.push({ tone: 'danger', text: many ? `${spent.length} exhausted` : reset ? `exhausted, resets in ${reset}` : 'exhausted' });
  }
  const uncounted = new Map<string, number>();
  for (const entry of entries) {
    if (usable(entry)) continue;
    const words = !many
      ? uncountedWords(entry, now)
      : entry.status === 'throttled'
        ? 'rate limited'
        : entry.status === 'available' || entry.status === 'exhausted'
          ? 'out of date'
          : 'without figures';
    uncounted.set(words, (uncounted.get(words) ?? 0) + 1);
  }
  for (const [words, count] of uncounted) issues.push({ tone: 'quiet', text: many ? `${count} ${words}` : words });

  return {
    entries,
    figure: countedWeight > 0 ? Math.round(sum / countedWeight) : null,
    left: countedWeight > 0 ? sum / 100 : null,
    size: sized ? { total: weight, counted: countedWeight } : null,
    weight,
    issues,
  };
}

/** When the soonest-resetting window at zero comes back, as a countdown, or null. */
function nextReset(entry: MachineCapacityView, now: number): string | null {
  let soonest: number | null = null;
  for (const window of entry.windows) {
    if (!isPlanWide(window) || window.resetsAt === null) continue;
    const at = Date.parse(window.resetsAt);
    if (Number.isFinite(at) && at > now && (soonest === null || at < soonest)) soonest = at;
  }
  return soonest === null ? null : untilShort(soonest, now);
}

/** The figure's colour: plain while there is room, amber at 30 or under, red at 10 or under. */
function figureTone(figure: number): Tone | undefined {
  return figure <= 10 ? 'danger' : figure <= 30 ? 'warn' : undefined;
}

/**
 * One provider's cell in a machine's row: a bar of one segment per account, what is left in
 * base allowances, a note and the plan size. Bar length is plan size on one scale for the whole column,
 * so a 5x machine's bar is a quarter of a 20x one's.
 */
function ProviderCell({ provider, summary, scale, row, now }: { provider: CapacityProvider; summary: ProviderSummary; scale: number; row: MachineListRow; now: number }) {
  const name = PROVIDER_NAMES[provider];
  const label = (
    <span className="fleet-provider-label">
      <i className="fleet-provider-mark" data-provider={provider} aria-hidden />
      {name}
    </span>
  );
  if (summary.entries.length === 0) {
    const installed = row.installedHarnesses.includes(provider);
    return (
      <span className="fleet-provider" data-provider={provider}>
        {label}
        <span className="fleet-provider-line fleet-provider-main">
          <span className="fleet-provider-empty">{installed ? (row.capacitySource ? 'not reported yet' : 'not reported') : 'not installed'}</span>
        </span>
        <span className="fleet-provider-line fleet-provider-note" />
      </span>
    );
  }
  const danger = summary.issues.filter((issue) => issue.tone === 'danger');
  // Red notes first and alone: the grey ones are already the grey segments of the bar, and a
  // note that reads "Fable closed +1" says one more model is closed when it is not.
  const shown = danger.length > 0 ? danger : summary.issues;
  const note =
    shown.length > 0
      ? shown.map((issue) => issue.text).join(' · ')
      : summary.entries.length > 1
        ? `${summary.entries.length} accounts`
        : planName(summary.entries[0]!);
  // In base allowances when any plan names its size, else the share left. "of 20x counted" when
  // the figure covers only part of the plans; with nothing counted there is no figure to qualify.
  const figureText =
    summary.figure === null || summary.left === null ? '—' : summary.size === null ? `${summary.figure}%` : allowances(summary.left);
  const sizeText =
    summary.size === null
      ? ''
      : summary.figure === null
        ? `${summary.size.total}x`
        : summary.size.counted === summary.size.total
          ? `of ${summary.size.total}x`
          : `of ${summary.size.counted}x counted`;
  const unsized = summary.size !== null && summary.entries.some((entry) => planSize(entry) === null);
  const tip = [
    summary.figure === null || summary.left === null
      ? `${name}: no current figures`
      : summary.size === null
        ? `${name}: ${summary.figure}% left across the accounts with current figures`
        : `${name}: ${allowances(summary.left)} left of ${summary.size.counted}x with current figures (${summary.figure}%)` +
          (summary.size.counted === summary.size.total ? '' : `; ${summary.size.total}x in all`),
    ...(unsized ? ['A plan whose size is unknown counts as 1x.'] : []),
    ...(row.capacitySource ? [`Accounts of ${row.capacitySource}, reached through its proxy; the floors are this machine's own.`] : []),
    ...summary.issues.map((issue) => issue.text),
    ...summary.entries.map((entry) => {
      const remaining = accountRemaining(entry, now);
      const head = `${entry.planLabel ?? 'plan unknown'} ${accountTag(entry)}`;
      if (usable(entry)) return `${head}: ${remaining === null ? 'no figures' : `${Math.round(remaining)}% left`}`;
      return `${head}: ${uncountedWords(entry, now)}${remaining === null ? '' : `, last ${Math.round(remaining)}%`}, not counted`;
    }),
  ].join('\n');
  return (
    <span className="fleet-provider" data-provider={provider} title={tip}>
      {label}
      <span className="fleet-provider-line fleet-provider-main">
        <span className="fleet-bar" style={{ width: `${Math.min(100, (summary.weight / scale) * 100)}%` }} aria-hidden>
          {summary.entries.map((entry) => {
            const remaining = accountRemaining(entry, now);
            const floor = segmentFloor(row.capacityPolicy, entry, now);
            const counted = usable(entry);
            const tone: Tone = !counted
              ? 'quiet'
              : remaining === 0 || underFloor(row.capacityPolicy, entry, now)
                ? 'danger'
                : remainingTone(remaining);
            return (
              <span
                key={entry.account}
                className="fleet-bar-segment"
                data-figures={remaining === null ? 'none' : counted ? 'counted' : 'uncounted'}
                style={{ flexGrow: planSize(entry) ?? 1 }}
              >
                {remaining === null ? null : <span className="fleet-bar-fill" data-tone={tone} style={{ width: `${remaining}%` }} />}
                {floor === null ? null : <span className="fleet-bar-floor" style={{ left: `${floor}%` }} />}
              </span>
            );
          })}
        </span>
        <span className="fleet-figure" data-tone={summary.figure === null ? 'quiet' : figureTone(summary.figure)}>
          {figureText}
        </span>
      </span>
      <span className="fleet-provider-line fleet-provider-note">
        <span className="fleet-note" data-tone={danger.length > 0 ? 'danger' : undefined}>
          {note}
        </span>
        <span className="fleet-size">{sizeText}</span>
      </span>
    </span>
  );
}

/** Which column of the capacity grid a window sits in: session, the longer plan windows, or per model and the rest. */
function windowColumn(window: CapacityWindowView): 0 | 1 | 2 {
  if (!isPlanWide(window)) return 2;
  return window.kind === 'session' ? 0 : 1;
}

/**
 * Every account's windows on one grid: an account per row, its session, longer plan windows and
 * per-model windows in fixed columns, so a floor handle at 25% sits at the same x on every
 * account. A cell holds as many windows as the account reports in that class.
 */
function CapacityGrid({
  row,
  now,
  notchFor,
}: {
  row: MachineListRow;
  now: number;
  notchFor: (entry: MachineCapacityView, window: CapacityWindowView) => FloorNotchProps | undefined;
}) {
  return (
    <div className="capacity-grid">
      <span className="capacity-grid-head">Account</span>
      <span className="capacity-grid-head">Session</span>
      <span className="capacity-grid-head">Week</span>
      <span className="capacity-grid-head">Per model and other</span>
      {row.capacity.map((entry) => {
        const path = row.capacityPaths.find((candidate) => candidate.harness === entry.provider);
        const shown = entry.windows.filter((window) => remainingNow(window, now) !== null);
        const columns: CapacityWindowView[][] = [[], [], []];
        for (const window of shown) columns[windowColumn(window)]!.push(window);
        return (
          <div key={`${entry.provider}:${entry.account}`} className="capacity-grid-account" data-fresh={entry.fresh ? 'true' : 'false'}>
            <CapacityAccountCell entry={entry} now={now} />
            {shown.length === 0 ? (
              <p className="capacity-grid-none">No figures: {statusWords(entry)}.</p>
            ) : (
              columns.map((windows, column) => (
                <div key={column} className="capacity-grid-cell" data-empty={windows.length === 0 ? 'true' : undefined}>
                  {windows.length === 0 ? (
                    <span className="capacity-grid-dash">—</span>
                  ) : (
                    windows.map((window) => (
                      <CapacityWindowMeter
                        key={window.name}
                        entry={entry}
                        window={window}
                        now={now}
                        policy={row.capacityPolicy}
                        path={path}
                        labelled={column === 2 || (column === 1 && window.kind !== 'weekly')}
                        notch={notchFor(entry, window)}
                      />
                    ))
                  )}
                </div>
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}

function CapacityAccountCell({ entry, now }: { entry: MachineCapacityView; now: number }) {
  const live = usable(entry);
  const remaining = accountRemaining(entry, now);
  const status = live
    ? `${entry.status === 'exhausted' ? 'exhausted · ' : ''}${relativeShort(entry.collectedAt, now)} ago`
    : entry.windows.length > 0 && entry.status === 'throttled'
      ? `rate limited, figures ${relativeShort(entry.collectedAt, now)} old`
      : uncountedWords(entry, now);
  return (
    <div
      className="capacity-grid-cell capacity-grid-who"
      title={`account ${entry.account} · collected ${absoluteTime(entry.collectedAt)} · ${entry.confidence} confidence${live ? '' : ' · not counted'}`}
    >
      <span className="capacity-grid-plan">
        <i className="fleet-provider-mark" data-provider={entry.provider} aria-hidden />
        <span className="capacity-grid-plan-name">
          {PROVIDER_NAMES[entry.provider]} {entry.planLabel ?? 'plan unknown'}
        </span>
        {remaining === null ? null : (
          <span className="capacity-grid-plan-figure" data-tone={live ? headlineTone(entry, remaining) : 'quiet'}>
            {Math.round(remaining)}%
          </span>
        )}
      </span>
      <span className="capacity-grid-status">
        <span className="mono">{accountTag(entry)}</span> · {status}
      </span>
    </div>
  );
}

/** One window: its bar with the floor handle on it, the figure, and when it resets. */
function CapacityWindowMeter({
  entry,
  window,
  now,
  policy,
  path,
  labelled,
  notch,
}: {
  entry: MachineCapacityView;
  window: CapacityWindowView;
  now: number;
  policy: MachineCapacityPolicyView;
  path: MachineCapacityPathView | undefined;
  /** Name the window under its bar, for the columns whose heading does not. */
  labelled: boolean;
  notch: FloorNotchProps | undefined;
}) {
  const remaining = remainingNow(window, now) ?? 0;
  const floor = floorOf(policy, entry, window);
  const live = usable(entry);
  const under = live && floor !== null && remaining < floor;
  const tone: Tone = !live ? 'quiet' : under || remaining <= 0 ? 'danger' : remainingTone(remaining);
  const modelClosed = windowModelClosed(path, window);
  const resetAt = window.resetsAt ? Date.parse(window.resetsAt) : Number.NaN;
  const reset = Number.isFinite(resetAt) ? (resetAt <= now ? 'reset' : `resets in ${untilShort(resetAt, now)}`) : '';
  return (
    <div
      className="capacity-window"
      data-closed={modelClosed ? 'true' : undefined}
      title={`${windowLabel(window)}: ${Math.round(remaining)}% left${reset ? `, ${reset}` : ''} · floor ${floor === null ? 'off' : `${floor}%`}${modelClosed ? ` · ${window.model} is closed on this machine and not offered` : ''}${live ? '' : ' · not counted'}`}
    >
      <span className="capacity-window-bar">
        <MeterTrack remaining={remaining} tone={tone} notch={notch} />
        <span className="capacity-meter-pct" data-tone={tone}>
          {Math.round(remaining)}%
        </span>
      </span>
      <span className="capacity-window-meta">
        <span className="capacity-window-label" data-labelled={labelled ? 'true' : undefined}>
          {windowLabel(window)}
        </span>
        {modelClosed ? <span className="capacity-meter-model-closed">closed</span> : null}
        {reset ? <span className="capacity-window-reset">{reset}</span> : null}
      </span>
    </div>
  );
}

/* ── fleet strip ─────────────────────────────────────────────────────────────────────── */

/**
 * One line across the top: how much of the fleet is alive and busy, and each distinct
 * provider account's headline. Accounts are keyed by their digest, so the same login on two
 * machines is one entry naming both, which is also how routing sees it.
 */
function FleetStrip({ items, now }: { items: MachineListRow[]; now: number }) {
  const live = items.filter((row) => lampFor(row, now).tone === 'ok').length;
  const paused = items.filter((row) => row.paused).length;
  const workersUsed = items.reduce((sum, row) => sum + row.currentAttempts.length, 0);
  const workersTotal = items.reduce((sum, row) => sum + row.maxWorkers, 0);
  const sidecarsUsed = items.reduce((sum, row) => sum + row.currentSidecars.length, 0);
  const sidecarsTotal = items.reduce((sum, row) => sum + row.maxSidecars, 0);
  const accounts = new Map<string, { entry: MachineCapacityView; machines: string[] }>();
  for (const row of items) {
    for (const entry of row.capacity) {
      const key = `${entry.provider}:${entry.account}`;
      const existing = accounts.get(key);
      if (existing) {
        existing.machines.push(row.name);
        if (entry.collectedAt > existing.entry.collectedAt) existing.entry = entry;
      } else {
        accounts.set(key, { entry, machines: [row.name] });
      }
    }
  }
  return (
    <div className="fleet-strip" role="group" aria-label="Fleet summary">
      <dl className="fleet-strip-counts">
        <div>
          <dt>Machines</dt>
          <dd>
            {items.length}
            <small>
              {live} live{paused > 0 ? `, ${paused} paused` : ''}
            </small>
          </dd>
        </div>
        <div>
          <dt>Workers</dt>
          <dd>
            {workersUsed} / {workersTotal}
            <small>in use</small>
          </dd>
        </div>
        <div>
          <dt>Sidecars</dt>
          <dd>
            {sidecarsUsed} / {sidecarsTotal}
            <small>in use</small>
          </dd>
        </div>
      </dl>
      {accounts.size > 0 ? (
        <ul className="fleet-strip-accounts" aria-label="Provider accounts">
          {[...accounts.values()].map(({ entry, machines }) => {
            const lowest = lowestRemaining(entry, now);
            const size = planSize(entry);
            return (
              <li key={`${entry.provider}:${entry.account}`} className="fleet-strip-account"
                title={`${machines.join(', ')}${lowest === null ? '' : `\n${Math.round(lowest)}% left`}`}
              >
                <span className="fleet-strip-account-name">
                  {PROVIDER_NAMES[entry.provider]}
                  {entry.planLabel ? ` ${entry.planLabel}` : ''}
                </span>
                <span className="fleet-strip-account-figure" data-tone={headlineTone(entry, lowest)}>
                  {lowest === null ? statusWords(entry) : size === null ? `${Math.round(lowest)}% left` : `${allowances((size * lowest) / 100)} left`}
                </span>
                <span className="fleet-strip-account-machines">
                  {machines.length === 1 ? machines[0] : `${machines.length} machines`}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="fleet-strip-note">
          No plan capacity reported. Enable <code className="mono">[capacity]</code> in a runner's{' '}
          <code className="mono">runner.toml</code> to see each provider account's remaining allowance here.
        </p>
      )}
    </div>
  );
}

/* ── panel ───────────────────────────────────────────────────────────────────────────── */

const NO_PROJECTS: UiProject[] = [];

export function MachinesSettingsPanel({ user }: { user: SessionUser | null }) {
  const live = useLive();
  const now = live.tick;
  const [page, setPage] = useState<MachineList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const poke = `${live.snapshot?.revision ?? 0}:${live.lastContactAt}`;

  const load = useCallback(async () => {
    try {
      setPage(await api.machines());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, poke]);

  if (error && !page) {
    return (
      <Empty
        title="Machines could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!page) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
        <Spinner size={20} />
      </div>
    );
  }

  const projectSlug = live.projectSlug;
  const projects = live.snapshot?.projects ?? NO_PROJECTS;

  return (
    <div className="machines-settings-panel">
      <section className="sheet-panel">
        <header className="panel-head">
          <Kicker count={page.items.length}>Machines</Kicker>
        </header>
        <div className="sheet-panel-body">
          <p className="cfg-help machines-intro">
            Each agent host runs the Nightshift runner and the coding CLIs. Health is from heartbeats;
            load is current attempts against worker slots; plan capacity is what each signed-in
            provider account has left, as the scheduler ranks it. Work totals are the last seven
            UTC days.{' '}
            <a
              className="linkish"
              href={projectSlug ? `/p/${encodeURIComponent(projectSlug)}/settings?group=agents` : '/settings?group=agents'}
            >
              Configure project routes
            </a>
          </p>
          {page.items.length === 0 ? (
            <p className="sheet-note">No machine has registered yet. Add one below and run the installer.</p>
          ) : (
            <>
              <FleetStrip items={page.items} now={now} />
              <FleetTable items={page.items} now={now} user={user} projects={projects} />
            </>
          )}
        </div>
      </section>
      <AddMachinePanel user={user} onCreated={() => void load()} />
    </div>
  );
}

function AddMachinePanel({
  user,
  onCreated,
}: {
  user: SessionUser | null;
  onCreated: () => void;
}) {
  const store = useStore();
  const canWrite = canWriteSettings(user);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState<MintedMachineResponse | null>(null);

  if (!canWrite && user?.role === 'member') {
    return <p className="sheet-note">Only an admin can add a machine and download an installer.</p>;
  }

  const create = async () => {
    setBusy(true);
    try {
      const created = await api.createMachine({ name });
      setMinted(created);
      setName('');
      store.toast('good', `${created.name} minted. Download the installer now — the token is not shown again.`);
      onCreated();
    } catch (caught) {
      store.toast('bad', caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="machine-add">
      {minted ? (
        <section className="sheet-panel machine-once">
          <header className="panel-head">
            <Kicker>Download the installer</Kicker>
          </header>
          <div className="sheet-panel-body">
            <p className="sheet-note">
              Run the script on the new box as a user that already has Node ≥ 22 and at least one
              agent CLI (<code className="mono">claude</code>, <code className="mono">codex</code>,{' '}
              <code className="mono">cursor-agent</code>, or <code className="mono">kimi</code>) on
              PATH. The file contains the runner token for <code className="mono">{minted.name}</code>
              . It is not stored in plaintext. The Windows installer is interactive-only and
              starts at that user's logon; use the service installer for a durable Windows service.
            </p>
            <pre className="connect-snippet" tabIndex={0}>
              {minted.token}
            </pre>
            <div className="connect-actions">
              <button
                type="button"
                className="btn btn-accent btn-sm"
                onClick={() => downloadText(minted.installers.windows.filename, minted.installers.windows.text)}
              >
                Windows installer
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => downloadText(minted.installers.linux.filename, minted.installers.linux.text)}
              >
                Linux installer
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => downloadText(minted.installers.toml.filename, minted.installers.toml.text)}
              >
                runner.toml
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() =>
                  void copyText(minted.token).then((ok) =>
                    store.toast(ok ? 'good' : 'bad', ok ? 'Token copied.' : 'Copy failed.'),
                  )
                }
              >
                Copy token
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setMinted(null)}>
                Dismiss
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="sheet-panel">
        <header className="panel-head">
          <Kicker>Add machine</Kicker>
        </header>
        <div className="sheet-panel-body">
          <div className="machine-add-row">
            <label className="cfg-field machine-add-field">
              <span className="cfg-label">Name</span>
              <input
                className="input"
                value={name}
                disabled={busy}
                placeholder="gpu-box"
                autoComplete="off"
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn-accent btn-sm"
              disabled={busy || name.trim().length === 0}
              onClick={() => void create()}
            >
              {busy ? 'Minting…' : 'Create and download installer'}
            </button>
          </div>
          <p className="cfg-help" style={{ margin: 'var(--s2) 0 0' }}>
            Creates the registry row and a one-shot installer. Lowercase letters, digits, underscores
            or hyphens. The new box appears above as offline until the installer registers and
            heartbeats.
          </p>
        </div>
      </section>
    </div>
  );
}

/* ── fleet table ─────────────────────────────────────────────────────────────────────── */

/**
 * The fleet as a table: one row per machine, every row the same height whatever the machine
 * has, and the detail (running work, every capacity window with its floor, controls, stats,
 * hardware) in a panel under the row that opens on demand. A card grid made a machine with
 * five accounts three times the height of one with one.
 */
function FleetTable({ items, now, user, projects }: { items: MachineListRow[]; now: number; user: SessionUser | null; projects: UiProject[] }) {
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set());
  const summaries = new Map(items.map((row) => [row.id, CAPACITY_PROVIDERS.map((provider) => summariseProvider(row, provider, now))]));
  // One scale per column: full width is the largest machine's plan total for that provider.
  const scale = CAPACITY_PROVIDERS.map((_, index) => Math.max(1, ...items.map((row) => summaries.get(row.id)![index]!.weight)));
  const toggle = (id: number): void =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <div className="fleet-table">
      <div className="fleet-grid fleet-head" aria-hidden>
        <span>Machine</span>
        <span>Load</span>
        <span className="fleet-providers">
          {CAPACITY_PROVIDERS.map((provider) => (
            <span key={provider} className="fleet-provider-label">
              <i className="fleet-provider-mark" data-provider={provider} />
              {PROVIDER_NAMES[provider]}
            </span>
          ))}
        </span>
        <span />
      </div>
      {items.map((row) => (
        <MachineRow
          key={row.id}
          row={row}
          now={now}
          user={user}
          projects={projects}
          summaries={summaries.get(row.id)!}
          scale={scale}
          open={open.has(row.id)}
          onToggle={() => toggle(row.id)}
        />
      ))}
      <p className="fleet-key">
        <span>
          <i className="fleet-key-swatch fleet-key-segments" aria-hidden />
          One segment per account. Bar length is plan size, on one scale per column (full width:{' '}
          {CAPACITY_PROVIDERS.map((provider, index) => `${scale[index]}x ${PROVIDER_NAMES[provider]}`).join(', ')}); the coloured part is what is left.
        </span>
        <span>
          <i className="fleet-key-swatch fleet-key-floor" aria-hidden />
          floor
        </span>
        <span>
          <i className="fleet-key-swatch fleet-key-uncounted" aria-hidden />
          not counted: rate limited or out of date
        </span>
        <span>
          <i className="fleet-key-swatch fleet-key-none" aria-hidden />
          no figures
        </span>
        <span>
          The figure is what is left across the counted accounts in base allowances, where 1.0x is a whole 1x plan; "of 20x counted" means only 20x of
          plans had current figures.
        </span>
      </p>
    </div>
  );
}

/** The scope as a chip: the first project's name, and how many more. */
function scopeChip(scope: NonNullable<MachineListRow['projectScope']>): string {
  const more = scope.projects.length - 1 + scope.hiddenCount;
  const first = scope.projects[0]?.name;
  if (!first) return plural(scope.hiddenCount, 'project');
  return more > 0 ? `${first} +${more}` : first;
}

/** What the machine is doing, in a few words: its first attempt, and a count of the rest. */
function activitySummary(row: MachineListRow): string {
  const [first, ...rest] = row.currentAttempts;
  if (first) {
    const more = rest.length + row.currentSidecars.length;
    return `${first.ticketKey} ${first.state}${more > 0 ? ` +${more}` : ''}`;
  }
  if (row.currentSidecars.length > 0) return plural(row.currentSidecars.length, 'sidecar job');
  return 'idle';
}

function MachineRow({
  row,
  now,
  user,
  projects,
  summaries,
  scale,
  open,
  onToggle,
}: {
  row: MachineListRow;
  now: number;
  user: SessionUser | null;
  projects: UiProject[];
  summaries: ProviderSummary[];
  scale: number[];
  open: boolean;
  onToggle: () => void;
}) {
  const canWrite = canWriteSettings(user);
  const store = useStore();
  const [rotatedToken, setRotatedToken] = useState<string | null>(null);
  const [tab, setTab] = useState<'capacity' | 'controls' | 'stats' | 'hardware'>('capacity');
  const [actionBusy, setActionBusy] = useState(false);
  const lamp = lampFor(row, now);
  const last = lastWork(row.stats, now);
  const registered =
    row.registeredAt > 0 ? `${relativeShort(row.registeredAt, now)} ago` : 'unknown';
  const seen = row.lastSeen == null ? 'never seen' : `seen ${relativeShort(row.lastSeen, now)} ago`;
  // What the stepper shows: the operator's request when there is one, otherwise what the
  // runner reports. Showing the request is the point — it survives the restart it triggers.
  const requested = row.desiredWorkers ?? row.maxWorkers;
  const requestedSidecars = row.desiredSidecars ?? row.maxSidecars;
  const run = <T,>(work: Promise<T>, onDone?: (value: T) => void): void => {
    setActionBusy(true);
    void work
      .then((value) => onDone?.(value))
      .catch((caught) => store.toast('bad', caught instanceof Error ? caught.message : String(caught)))
      .finally(() => setActionBusy(false));
  };
  const setWorkers = (next: number): void =>
    run(api.setMachineWorkers(row.id, next), () =>
      store.toast('good', `${row.name}: ${next} ${next === 1 ? 'worker' : 'workers'} requested.`),
    );
  const setSidecars = (next: number): void =>
    run(api.setMachineSidecars(row.id, next), () =>
      store.toast('good', `${row.name}: ${next} sidecar ${next === 1 ? 'slot' : 'slots'} requested.`),
    );
  const setPaused = (next: boolean): void =>
    run(api.setMachinePaused(row.id, next), (result) => store.toast('good', result.message));
  const commitPolicy = (policy: MachineCapacityPolicyView): void =>
    run(api.setMachineCapacityPolicy(row.id, policy), (result) => store.toast('good', result.message));

  // Every plan window carries a notch of its own, keyed by provider and window name. Moving
  // one sends the whole map back with that key changed (or removed, for off).
  const notchFor = (entry: MachineCapacityView, window: CapacityWindowView): FloorNotchProps | undefined => {
    if (!countsTowardPlan(window)) return undefined;
    // Each account has its own notch. An account without its own key shows, and is judged by,
    // the provider-wide floor; moving the notch gives it a key of its own and leaves the others.
    const key = floorKeyOf(entry, window);
    const shared = row.capacityPolicy.floors[providerFloorKeyOf(entry, window)];
    return {
      label: `${windowLabel(window)} floor`,
      value: floorOf(row.capacityPolicy, entry, window),
      editable: canWrite && !actionBusy,
      machine: row.name,
      onCommit: (next) => {
        const floors = { ...row.capacityPolicy.floors };
        // Off for this account: a 0 overrides a provider-wide floor; with none, drop the key.
        if (next === null) {
          if (shared !== undefined && shared > 0) floors[key] = 0;
          else delete floors[key];
        } else floors[key] = next;
        commitPolicy({ ...row.capacityPolicy, floors });
      },
    };
  };

  const detailId = `machine-${row.id}-detail`;
  const versionWords = row.versionStatus === 'outdated' ? 'outdated runner' : row.versionStatus === 'patch' ? 'runner behind' : null;
  const tabs = ['capacity', 'controls', 'stats', 'hardware'] as const;
  const tabNames = { capacity: 'Capacity & floors', controls: 'Controls', stats: 'Stats', hardware: 'Hardware' };
  // The row's second line: what is wrong with the machine first, then what it is doing.
  const subParts: ReactNode[] = [];
  if (lamp.label !== 'live') subParts.push(<span data-tone={lamp.tone}>{lamp.label}</span>);
  if (!row.live) subParts.push(seen);
  if (versionWords) subParts.push(<span data-tone={row.versionStatus === 'outdated' ? 'attention' : 'quiet'}>{versionWords}</span>);
  const activity = activitySummary(row);
  if (row.live || activity !== 'idle') subParts.push(activity);
  if (row.capacitySource) subParts.push(`via ${row.capacitySource}`);

  return (
    <div className="fleet-row" data-machine={row.name} data-open={open ? 'true' : undefined} data-paused={row.paused ? 'true' : undefined}>
      <button type="button" className="fleet-grid fleet-row-summary" aria-expanded={open} aria-controls={detailId} onClick={onToggle}>
        <span className="fleet-machine">
          <span className="fleet-machine-line">
            <span
              className={`lamp${lamp.tone === 'attention' ? ' lamp-breathe' : ''}`}
              style={{ background: lampColour(lamp.tone) }}
              aria-hidden
            />
            <span className="fleet-machine-name mono">{row.name}</span>
            {row.projectScope ? (
              <span className="fleet-chip" title={`${scopeSummary(row.projectScope)}. Other projects' work goes to other machines.`}>
                {scopeChip(row.projectScope)}
              </span>
            ) : null}
          </span>
          <span className="fleet-machine-sub">
            {subParts.map((part, index) => (
              <Fragment key={index}>
                {index > 0 ? ' · ' : ''}
                {part}
              </Fragment>
            ))}
          </span>
        </span>
        <span className="fleet-load">
          <span className="fleet-load-line">
            <b>{row.currentAttempts.length}</b>/{row.maxWorkers} {row.maxWorkers === 1 ? 'worker' : 'workers'}
          </span>
          {/*
            Its own figure beside the worker load, never added to it. Sidecar slots exist so
            cheap background work cannot crowd out ticket work; a combined "5 of 6 busy" would
            hide exactly the thing the two counts are there to keep apart.
          */}
          <span className="fleet-load-line" title="Sidecar slots are a separate lane from workers.">
            {row.maxSidecars === 0 ? 'no sidecars' : `${row.currentSidecars.length}/${row.maxSidecars} sidecar${row.maxSidecars === 1 ? '' : 's'}`}
          </span>
        </span>
        <span className="fleet-providers">
          {CAPACITY_PROVIDERS.map((provider, index) => (
            <ProviderCell key={provider} provider={provider} summary={summaries[index]!} scale={scale[index]!} row={row} now={now} />
          ))}
        </span>
        <span className="fleet-chevron" aria-hidden>
          {open ? '▾' : '▸'}
        </span>
      </button>

      <div className="fleet-detail" id={detailId} hidden={!open}>
        <div className="machine-card-head">
          <p className="machine-card-meta">
            <span data-tone={lamp.tone} className="machine-card-health">
              {lamp.label}
            </span>{' '}
            · <span title={row.lastSeen == null ? 'Never seen' : absoluteTime(row.lastSeen)}>{seen}</span> · {row.os}
            {row.version ? ` · ${row.version}` : ' · version unknown'} · registered {registered}
            {row.installedHarnesses.length > 0 ? ` · ${row.installedHarnesses.join(', ')}` : ' · no harness advertised'}
          </p>
          {row.projectScope ? (
            <p className="machine-card-scope">
              {scopeSummary(row.projectScope)}. Other projects' work goes to other machines.
            </p>
          ) : null}
          {row.versionStatus === 'outdated' || row.versionStatus === 'patch' ? (
            <p
              className="machine-card-version"
              data-tone={row.versionStatus === 'outdated' ? 'attention' : 'quiet'}
              title={`Runner reports ${row.version ?? 'no version'}; orchestrator is ${row.expectedVersion}`}
            >
              {row.versionStatus === 'outdated'
                ? `Outdated: expected ${row.expectedVersion}. Refused work until it updates.`
                : `Behind: expected ${row.expectedVersion}.`}
            </p>
          ) : null}
        </div>

        {row.deviceLeases?.length || row.attachedDevices?.length ? (
          <section className="machine-card-section" aria-label="Attached device reservations">
            <h3 className="machine-card-legend">Attached devices</h3>
            {row.attachedDevices?.map(device => (
              <p key={device.id}>Android {device.id.slice(0, 12)} · {device.state}{' '}
                <button className="btn btn-sm" onClick={() => { void copyText(device.id).then(ok => store.toast(ok ? 'good' : 'bad', ok ? 'Device ID copied' : 'Could not copy device ID')); }}>Copy device ID</button>
              </p>
            ))}
            {row.deviceLeases?.map(lease => (
              <p key={lease.attemptId}>
                Android reserved by {lease.ticketKey}, attempt {lease.attemptId}
                {canWrite ? <button className="btn btn-sm" disabled={actionBusy || !row.paused || row.currentAttempts.some(attempt => attempt.id === lease.attemptId)} onClick={() => {
                  if (!window.confirm(`Confirm the device process for attempt ${lease.attemptId} has stopped on ${row.name}. Releasing permits another attempt to use the phone.`)) return;
                  run(api.releaseDeviceLease(row.id, lease.attemptId), result => store.toast('good', result.message));
                }}>Release stopped owner</button> : null}
              </p>
            ))}
            <p className="machine-card-meta">Unresolved owners retain their reservation. Pause the machine and stop the owning attempt before release.</p>
          </section>
        ) : null}

        {row.currentAttempts.length > 0 || row.currentSidecars.length > 0 ? (
          <section className="machine-card-section">
            <h3 className="machine-card-legend">Running</h3>
            <ul className="machine-card-attempts">
              {row.currentAttempts.map((attempt) => (
                <CurrentAttemptRow key={attempt.id} attempt={attempt} now={now} />
              ))}
              {row.currentSidecars.map((job) => (
                <li key={`sidecar-${job.id}`} className="machine-card-attempt">
                  <a className="mono" href={ticketHref(job.ticketKey)}>
                    {job.ticketKey}
                  </a>
                  <span className="machine-card-attempt-status">
                    sidecar ·{' '}
                    {job.kind === 'footprint'
                      ? 'footprint'
                      : job.kind === 'brief-cleanup'
                        ? 'brief cleanup'
                        : job.kind === 'text-task'
                          ? 'text task'
                        : job.kind === 'creation-draft'
                          ? 'quick add'
                          : 'scan'}
                  </span>
                  <span className="machine-card-attempt-age">
                    {job.startedAt == null ? 'unstarted' : relativeShort(job.startedAt, now)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <div className="machine-card-tabs" role="tablist" aria-label={`${row.name} details`}>
          {tabs.map((name, index) => (
            <button
              key={name}
              type="button"
              role="tab"
              id={`machine-${row.id}-${name}-tab`}
              aria-controls={`machine-${row.id}-${name}-panel`}
              aria-selected={tab === name}
              tabIndex={tab === name ? 0 : -1}
              onClick={() => setTab(name)}
              onKeyDown={(event) => {
                const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length
                  : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length
                    : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
                if (next === null) return;
                event.preventDefault();
                setTab(tabs[next]!);
                document.getElementById(`machine-${row.id}-${tabs[next]}-tab`)?.focus();
              }}
            >
              {tabNames[name]}
            </button>
          ))}
        </div>

        <section className="machine-card-section machine-card-tab-panel" role="tabpanel" id={`machine-${row.id}-capacity-panel`} aria-labelledby={`machine-${row.id}-capacity-tab`} hidden={tab !== 'capacity'}>
          {row.capacitySource ? (
            <p className="machine-card-capacity-source">
              Shared from <strong>{row.capacitySource}</strong>: this machine spends through that machine's account proxy. The floors here are this machine's own.
            </p>
          ) : null}
          {row.capacityPaths
            .filter((path) => !path.open && path.reason)
            .map((path) => (
              <p key={path.harness} className="capacity-path-why">
                <span className="capacity-meter-path" data-tone="danger">
                  {PROVIDER_NAMES[path.harness as CapacityProvider] ?? path.harness} closed
                </span>{' '}
                {path.reason}
              </p>
            ))}
          {row.capacity.length > 0 ? (
            <>
              <CapacityGrid row={row} now={now} notchFor={notchFor} />
              {canWrite ? (
                <p className="machine-card-idle">
                  Drag a floor's handle, or focus it and use the arrow keys. Home or a double-click turns a floor off.
                </p>
              ) : null}
            </>
          ) : (
            <p className="machine-card-idle">
              {row.capacitySource
                ? `Not reported yet by ${row.capacitySource}.`
                : "Not reported. Telemetry is off in this runner's configuration."}
            </p>
          )}
        </section>

        <section className="machine-card-section machine-card-tab-panel" role="tabpanel" id={`machine-${row.id}-stats-panel`} aria-labelledby={`machine-${row.id}-stats-tab`} hidden={tab !== 'stats'}>
          <h3 className="machine-card-legend">Last {row.stats.windowDays} days UTC</h3>
          <dl className="machine-card-stats">
            <Stat label="Finished" value={String(row.stats.finished)} />
            <Stat label="Succeeded" value={String(row.stats.succeeded)} />
            <Stat
              label="Failed"
              value={String(row.stats.failed)}
              tone={row.stats.failed > 0 ? 'warn' : undefined}
            />
            <Stat
              label="Interrupted"
              value={String(row.stats.interrupted)}
              tone={row.stats.interrupted > 0 ? 'warn' : undefined}
            />
            <Stat label="Tokens" value={`${tokens(row.stats.tokensIn)} in · ${tokens(row.stats.tokensOut)} out`} />
            <Stat
              label="Spend"
              value={spendFigure(row.stats.spendUsd, row.stats.spendEntries)}
              note={
                row.stats.unknownSpendEntries > 0
                  ? `${plural(row.stats.unknownSpendEntries, 'unpriced entry')}`
                  : undefined
              }
            />
            <Stat label="Mean time" value={meanDuration(row.stats)} />
            <Stat
              label="Last work"
              value={last.value}
              note={last.note}
              href={row.stats.lastTicketKey ? ticketHref(row.stats.lastTicketKey) : undefined}
            />
            {row.stats.cancelled > 0 ? (
              <Stat label="Cancelled" value={String(row.stats.cancelled)} />
            ) : null}
          </dl>
        </section>

        <div className="machine-card-tab-panel" role="tabpanel" id={`machine-${row.id}-hardware-panel`} aria-labelledby={`machine-${row.id}-hardware-tab`} hidden={tab !== 'hardware'}>
          {row.hardware ? <HardwareSection hardware={row.hardware} now={now} /> : <p className="machine-card-idle">Hardware has not been reported yet.</p>}
        </div>

        <div className="machine-card-tab-panel" role="tabpanel" id={`machine-${row.id}-controls-panel`} aria-labelledby={`machine-${row.id}-controls-tab`} hidden={tab !== 'controls'}>
        <MachineLibraries row={row} canWrite={canWrite} />
        {row.storage !== undefined ? <MachineStorage row={row} canWrite={canWrite} now={now} /> : null}
        {canWrite ? (
          <MachineControls
            row={row}
            now={now}
            busy={actionBusy}
            requestedWorkers={requested}
            requestedSidecars={requestedSidecars}
            onWorkers={setWorkers}
            onSidecars={setSidecars}
            onPaused={setPaused}
            onRestart={() => {
              if (!window.confirm(`Restart the runner on "${row.name}"? It finishes what it is running first.`)) return;
              run(api.restartMachine(row.id), (result) => store.toast('good', result.message));
            }}
            onRotate={() =>
              run(api.rotateMachineToken(row.id), (result) => {
                setRotatedToken(result.token);
                void copyText(result.token).then((ok) =>
                  store.toast(ok ? 'good' : 'bad', ok ? 'Token rotated and copied.' : 'Token rotated.'),
                );
              })
            }
            onRevoke={() => {
              if (!window.confirm(`Revoke machine "${row.name}"? Its runner will stop authenticating.`)) return;
              run(api.revokeMachine(row.id), () => store.toast('good', `${row.name} revoked. Refresh to remove it.`));
            }}
            onPolicy={commitPolicy}
            onHeld={(held) => run(api.setMachineCapacityHeld(row.id, held), (result) => store.toast('good', result.message))}
            rotatedToken={rotatedToken}
            projects={projects}
          />
        ) : <p className="machine-card-idle">Only an admin can change machine controls.</p>}
        </div>
      </div>
    </div>
  );
}

/* ── controls ────────────────────────────────────────────────────────────────────────── */

function Stepper({
  label,
  value,
  min,
  max,
  disabled,
  name,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  name: string;
  onChange: (next: number) => void;
}) {
  return (
    <div className="control-field">
      <span className="control-label">{label}</span>
      <div className="stepper">
        <button
          type="button"
          className="btn btn-sm"
          aria-label={`Fewer ${label.toLowerCase()} on ${name}`}
          disabled={disabled || value <= min}
          onClick={() => onChange(value - 1)}
        >
          −
        </button>
        <span className="stepper-value mono" aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          className="btn btn-sm"
          aria-label={`More ${label.toLowerCase()} on ${name}`}
          disabled={disabled || value >= max}
          onClick={() => onChange(value + 1)}
        >
          +
        </button>
      </div>
    </div>
  );
}

/** Binary gigabytes, one decimal under a hundred. Memory, drives and VRAM all read this way. */
function gib(count: number): string {
  const value = count / 1024 ** 3;
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} GB`;
}

/**
 * What the machine is, collapsed to one line until opened: the card is about what the box is
 * doing, and its parts matter on the day you ask "which one has the GPU" or "why is that
 * runner out of disk". Everything shown is what the runner's OS reported; nothing is derived.
 */
function HardwareSection({ hardware, now }: { hardware: MachineHardwareView; now: number }) {
  const brief = [
    hardware.cpu ? hardware.cpu.model : null,
    hardware.memory ? gib(hardware.memory.totalBytes) : null,
    hardware.gpus[0]?.name ?? null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
  return (
    <section className="machine-card-section machine-card-hardware">
      <div className="machine-card-hardware-summary">
        <span className="machine-card-hardware-brief" title={brief}>
          {brief || 'reported, nothing readable'}
        </span>
        <span className="machine-card-hardware-age">{relativeShort(hardware.receivedAt, now)} ago</span>
      </div>
      <dl className="machine-card-stats machine-card-hardware-stats">
        {hardware.cpu ? <Stat label="CPU" value={hardware.cpu.model} note={plural(hardware.cpu.cores, 'core')} /> : null}
        {hardware.memory ? (
          <Stat
            label="Memory"
            value={gib(hardware.memory.totalBytes)}
            note={`${gib(hardware.memory.freeBytes)} free`}
          />
        ) : null}
        {hardware.gpus.map((gpu, index) => (
          <Stat key={`gpu-${index}`} label={hardware.gpus.length > 1 ? `GPU ${index + 1}` : 'GPU'} value={gpu.name} note={gpu.memoryBytes === null ? undefined : gib(gpu.memoryBytes)} />
        ))}
        {hardware.gpus.length === 0 ? <Stat label="GPU" value="none reported" /> : null}
        {hardware.drives.map((drive) => (
          <Stat
            key={`drive-${drive.mount}`}
            label={`Drive ${drive.mount}`}
            value={`${gib(drive.freeBytes)} free of ${gib(drive.totalBytes)}`}
            note={drive.filesystem ?? undefined}
            tone={drive.totalBytes > 0 && drive.freeBytes < drive.totalBytes * 0.1 ? 'warn' : undefined}
          />
        ))}
        {hardware.displays.map((display, index) => (
          <Stat
            key={`display-${index}`}
            label={hardware.displays.length > 1 ? `Display ${index + 1}` : 'Display'}
            value={`${display.width} × ${display.height}`}
            note={display.hertz === null ? undefined : `${display.hertz} Hz`}
          />
        ))}
        {hardware.displays.length === 0 ? <Stat label="Display" value="none reported" /> : null}
        {hardware.arch ? <Stat label="Architecture" value={hardware.arch} /> : null}
        {hardware.session ? (
          <Stat
            label="Session"
            value={hardware.session.graphics === 'available' ? 'can render' : hardware.session.graphics === 'unavailable' ? 'cannot render' : 'unknown'}
            note={hardware.session.detail ?? undefined}
            tone={hardware.session.graphics === 'unavailable' ? 'warn' : undefined}
          />
        ) : null}
      </dl>
      {hardware.toolchains.length > 0 ? (
        <dl className="machine-card-stats machine-card-toolchains" aria-label="Toolchains probed under the runner account">
          {hardware.toolchains.map((tool) => (
            <Stat
              key={tool.id}
              label={tool.id}
              value={tool.status === 'detected' ? tool.versions.slice(0, 3).join(', ') || 'detected' : tool.status === 'missing' ? 'not installed' : 'probe failed'}
              note={tool.status === 'detected' && tool.versions.length > 3 ? `+${tool.versions.length - 3} more` : tool.status === 'error' ? (tool.detail ?? undefined) : undefined}
              tone={tool.status === 'error' ? 'warn' : undefined}
            />
          ))}
        </dl>
      ) : (
        <p className="machine-card-idle">Toolchains have not been reported yet. Update the runner if its build does not support toolchain probes.</p>
      )}
    </section>
  );
}

/**
 * Everything an operator can do to a machine, in three rows: the two slot counts, the
 * capacity-floor summary with the auto-resume switch and the hold, and the actions. The floors
 * themselves are set on the bars (`FloorNotch`); they are what turn the plan telemetry into a
 * rule: under a floor, that provider's path is closed; with every path closed, the machine is
 * held and takes nothing until a path recovers (or, with auto-resume off, until Resume).
 */
function MachineControls({
  row,
  now,
  busy,
  requestedWorkers,
  requestedSidecars,
  onWorkers,
  onSidecars,
  onPaused,
  onRestart,
  onRotate,
  onRevoke,
  onPolicy,
  onHeld,
  rotatedToken,
  projects,
}: {
  row: MachineListRow;
  now: number;
  busy: boolean;
  requestedWorkers: number;
  requestedSidecars: number;
  onWorkers: (next: number) => void;
  onSidecars: (next: number) => void;
  onPaused: (next: boolean) => void;
  onRestart: () => void;
  onRotate: () => void;
  onRevoke: () => void;
  onPolicy: (policy: MachineCapacityPolicyView) => void;
  onHeld: (held: boolean) => void;
  rotatedToken: string | null;
  projects: UiProject[];
}) {
  const policy = row.capacityPolicy;
  const held = row.capacityHeldAt !== null;
  return (
    <section className="machine-card-section machine-card-controls">
      <div className="control-row">
        <Stepper label="Workers" value={requestedWorkers} min={1} max={MAX_WORKERS} disabled={busy} name={row.name} onChange={onWorkers} />
        <Stepper label="Sidecars" value={requestedSidecars} min={0} max={MAX_SIDECARS} disabled={busy} name={row.name} onChange={onSidecars} />
        <span className="control-gap" aria-hidden />
        <button
          type="button"
          className={row.paused ? 'btn btn-sm btn-primary' : 'btn btn-sm'}
          aria-pressed={row.paused}
          disabled={busy}
          title={
            row.paused
              ? 'Offer this machine new attempts and sidecar jobs again.'
              : 'Stop offering this machine new work. What it is running finishes; the runner keeps heartbeating.'
          }
          onClick={() => onPaused(!row.paused)}
        >
          {row.paused ? 'Resume' : 'Pause'}
        </button>
        <button
          type="button"
          className="btn btn-sm"
          disabled={busy || row.restartPending}
          title="Restart the runner. It finishes what it is running first."
          onClick={onRestart}
        >
          {row.restartPending ? 'Restart pending…' : 'Restart'}
        </button>
      </div>

      <div className="control-row control-row-access">
        <label className="control-field control-switch" title="Lift a hold on its own when a path clears its floor. Off: wait for Resume.">
          <span className="control-label">Auto-resume</span>
          <input
            type="checkbox"
            className="switch"
            checked={policy.autoResume}
            disabled={busy}
            onChange={(event) => onPolicy({ ...policy, autoResume: event.target.checked })}
          />
        </label>
        {held ? (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={busy}
            title="Lift the hold now. Paths still under a floor stay closed; the next report re-judges."
            onClick={() => onHeld(false)}
          >
            Resume from hold
          </button>
        ) : null}
        <span className="control-gap" aria-hidden />
        <button type="button" className="btn btn-sm" disabled={busy} title="Mint a new runner token; the old one stops working." onClick={onRotate}>
          Rotate token
        </button>
        <button type="button" className="btn btn-sm btn-quiet-danger" disabled={busy} title="Remove the machine from the registry. Its runner stops authenticating." onClick={onRevoke}>
          Revoke machine
        </button>
      </div>

      <MachineProjectsControl row={row} projects={projects} busy={busy} />

      {held ? <p className="machine-card-idle">Held since {relativeShort(row.capacityHeldAt!, now)} ago. {policy.autoResume ? 'Resumes when a path recovers.' : 'Waiting for Resume.'}</p> : null}
      {/* The gap between asked-for and actual is the whole reason both numbers exist. */}
      {row.desiredWorkers !== null && row.desiredWorkers !== row.maxWorkers ? (
        <p className="machine-card-idle">
          {row.desiredWorkers} requested; the runner is still reporting {row.maxWorkers}. It restarts
          itself on its next heartbeat to take the change up.
        </p>
      ) : null}
      {/*
        Once the runner has taken the request up the two numbers agree, and the card used to
        go quiet — so a machine running one worker looked identical whether that came from
        its own runner.toml or from somebody setting it here weeks ago. On 2026-09-09 it was
        the second, and the queue behind it was read as a scheduler fault for hours.
      */}
      {row.desiredWorkers !== null && row.desiredWorkers === row.maxWorkers ? (
        <p className="machine-card-idle">
          {row.maxWorkers} {row.maxWorkers === 1 ? 'worker' : 'workers'} set from this page, not by
          the machine's own <code>runner.toml</code>. Change it here to change it.
        </p>
      ) : null}
      {row.desiredSidecars !== null && row.desiredSidecars !== row.maxSidecars ? (
        <p className="machine-card-idle">
          {row.desiredSidecars} sidecar {row.desiredSidecars === 1 ? 'slot' : 'slots'} requested;
          the runner is still reporting {row.maxSidecars}. It drains active work and restarts
          itself on its next heartbeat to take the change up.
        </p>
      ) : null}
      {rotatedToken ? (
        <p className="sheet-note">
          New token (shown once): <code className="mono">{rotatedToken}</code>
        </p>
      ) : null}
    </section>
  );
}

/** The machine's project scope in words: "All projects", or "Only Helsing". */
function scopeSummary(scope: MachineListRow['projectScope']): string {
  if (scope === null) return 'All projects';
  const names = scope.projects.map((project) => project.name);
  if (scope.hiddenCount > 0) names.push(`${scope.hiddenCount} other ${scope.hiddenCount === 1 ? 'project' : 'projects'}`);
  return `Only ${names.join(', ')}`;
}

/**
 * Keep the machine for some projects. Collapsed by default: nearly every machine serves every
 * project, and the list of projects would otherwise be the tallest thing on the card.
 */
function MachineProjectsControl({ row, projects, busy }: { row: MachineListRow; projects: UiProject[]; busy: boolean }) {
  // Slugs in the project list's order, so a saved scope compares equal to the draft it came from.
  const inOrder = (slugs: readonly string[]): string[] =>
    projects.filter((project) => slugs.includes(project.slug)).map((project) => project.slug);
  const { draft, setDraft, error } = useSettingsAutosave({
    value: { allProjects: row.projectScope === null, slugs: inOrder(row.projectScope?.projects.map((project) => project.slug) ?? []) },
    save: async (next) => {
      const ids = next.allProjects ? null : projects.filter((project) => next.slugs.includes(project.slug)).map((project) => project.id);
      await api.setMachineProjects(row.id, ids);
    },
    what: `${row.name} projects`,
    enabled: !busy,
    delay: 500,
    validate: (next) => (next.allProjects || next.slugs.length > 0 ? null : 'Select at least one project, or pause the machine.'),
  });
  return (
    <details className="machine-card-projects">
      <summary>
        <span className="control-label">Projects</span> {scopeSummary(row.projectScope)}
      </summary>
      <p className="machine-card-meta">
        A machine kept for some projects is offered only their attempts, reviews, sidecar jobs and
        night manager turns. Its queued work for other projects moves to other machines.
      </p>
      <ProjectAccessPicker
        projects={projects}
        allProjects={draft.allProjects}
        slugs={draft.slugs}
        disabled={busy}
        onChange={(next) => setDraft({ allProjects: next.allProjects, slugs: inOrder(next.slugs) })}
      />
      {error ? <p className="cfg-error" role="alert">Unsaved: {error}</p> : null}
    </details>
  );
}

function CurrentAttemptRow({ attempt, now }: { attempt: MachineAttemptView; now: number }) {
  const orphan =
    attempt.startedAt != null && now - attempt.startedAt > stageTimeoutMs(attempt.state, attempt.timeoutMs);
  const age =
    attempt.startedAt == null ? 'unstarted' : `${relativeShort(attempt.startedAt, now)}`;
  return (
    <li className="machine-card-attempt" data-orphan={orphan ? 'true' : undefined}>
      <a className="mono" href={ticketHref(attempt.ticketKey)}>
        {attempt.ticketKey}
      </a>
      <StateChip state={attempt.state} />
      <span className="machine-card-attempt-status">
        {orphan ? 'orphan' : attempt.status}
        {attempt.activity ? ` · ${attempt.activity}` : ''}
      </span>
      <span className="machine-card-attempt-age">{age}</span>
    </li>
  );
}

function Stat({
  label,
  value,
  note,
  tone,
  href,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'warn';
  href?: string;
}) {
  return (
    <div className="machine-stat" data-tone={tone}>
      <dt>{label}</dt>
      <dd>
        {href ? <a href={href}>{value}</a> : value}
        {note ? <small>{note}</small> : null}
      </dd>
    </div>
  );
}
